import { useId, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";

import {
  GUIDE_STEPS,
  guidedHelp,
  guidedInstructions,
  type GuideStep,
} from "../../content/guided-help";
import { useAuth } from "../../contexts/AuthContext";
import { useProjectCompleteness } from "../../lib/queries/projectCompleteness";
import { useProject, useProjects } from "../../lib/queries/projects";
import {
  useQuoteRequest,
  useQuoteRequestsByProject,
  useSendReadiness,
} from "../../lib/queries/quoteRequests";
import { scrollWorkspaceTarget } from "../../lib/scrollWorkspaceTarget";
import { tenantScope } from "../../lib/tenantScope";
import { SendBlockers } from "../../pages/quote-requests/_parts/SendBlockers";

import type { AssistTarget } from "./assist-target";
import {
  guideContext,
  guideDestination,
  guideHref,
  rememberGuide,
  isStartRequestPath,
} from "./guide-context";
import { useGuideReading } from "./guide-reading";

const button =
  "inline-flex min-h-11 items-center justify-center rounded-md border border-slate-300 px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:opacity-50";

type GuideContext = ReturnType<typeof guideContext>;
type GuideOptions = {
  presentation?: "page" | "sidebar";
  onContextChange?: (context: GuideContext) => void;
  onShowStep?: (to: string, target: AssistTarget, context: GuideContext) => void;
  onReadingChange?: () => void;
};
export function GuideSurface({
  context: selected,
  ...options
}: GuideOptions & { context?: GuideContext } = {}) {
  const location = useLocation();
  const { user, membershipStatus } = useAuth();
  const context = selected ?? guideContext(location.pathname, location.search);
  const scope = membershipStatus === "active" ? tenantScope() : null;
  if (isStartRequestPath(location.pathname)) {
    return <StartGuide onShowStep={scope ? options.onShowStep : undefined} />;
  }
  // Remount reading state on identity, cabinet or object change. Never carry it across tenants.
  return (
    <GuideContent
      key={`${user?.id}:${scope}:${context.projectId}:${context.requestId}:${context.companyId}`}
      scope={scope}
      userId={user?.id}
      {...context}
      {...options}
    />
  );
}
function GuideContent({
  scope,
  userId,
  projectId: requestedProject,
  requestId,
  companyId,
  presentation = "page",
  onContextChange,
  onShowStep,
  onReadingChange,
}: GuideOptions & {
  scope: string | null;
  userId?: string;
  projectId?: string;
  companyId?: string;
  requestId?: string;
}) {
  const { t, i18n } = useTranslation();
  const projectHeadingId = useId();
  const content = guidedHelp[i18n.language.startsWith("en") ? "en" : "fr"];
  const request = useQuoteRequest(scope ? requestId : undefined);
  const projectId = requestedProject ?? request.data?.project_id;
  const project = useProject(scope ? projectId : undefined);
  const completeness = useProjectCompleteness(scope ? projectId : undefined);
  const readiness = useSendReadiness(
    scope && request.data?.status === "draft" ? requestId : undefined,
  );
  const projects = useProjects(
    { limit: 100, company_id: companyId },
    { enabled: !!scope && !projectId && !requestId },
  );
  const requests = useQuoteRequestsByProject({
    projectId,
    enabled: !!scope && !!project.data && !requestId,
    limit: 100,
  });
  const key = `kv-guide-reading-v1:${userId}:${scope}:${requestId ? `request:${requestId}` : `project:${requestedProject ?? ""}`}`;
  const [step, setStep] = useGuideReading(key);
  const heading = useRef<HTMLHeadingElement>(null);
  const change = (next: GuideStep, focusHeading = true) => {
    onReadingChange?.();
    setStep(next);
    const target = heading.current;
    if (target && focusHeading) {
      const header = document.querySelector("[data-project-anchor-occluder]");
      target.style.scrollMarginTop = `${(header?.getBoundingClientRect().height ?? 0) + 16}px`;
      target.focus({ preventScroll: true });
      scrollWorkspaceTarget(target, presentation === "sidebar" ? 16 : undefined);
    }
  };
  const index = GUIDE_STEPS.indexOf(step);
  const mismatch = !!(
    requestedProject &&
    request.data &&
    request.data.project_id !== requestedProject
  );
  const blocked =
    request.isError ||
    project.isError ||
    completeness.isError ||
    readiness.isError ||
    mismatch ||
    !!(companyId && project.data && project.data.company_id !== companyId);
  const verified = !!scope && !!project.data && !blocked && (!requestId || !!request.data);
  const historical = !!request.data && request.data.status !== "draft";
  const chapters = historical ? { ...content.steps, followup: content.history } : content.steps;
  const chapter = chapters[step];
  const instructions = guidedInstructions[i18n.language.startsWith("en") ? "en" : "fr"][step];
  const recommended: GuideStep | undefined = historical
    ? "followup"
    : readiness.data?.workflow?.step;
  return (
    <section
      aria-label={t("guide.title")}
      className={
        presentation === "sidebar"
          ? "min-w-0 space-y-4"
          : "min-w-0 space-y-4 rounded-xl border border-slate-200 bg-white p-4 sm:p-5"
      }
    >
      {presentation === "page" && (
        <div>
          <h2 className="text-navy-950 text-lg font-semibold">{content.title}</h2>
          <p className="mt-2 text-sm text-slate-600">{content.intro}</p>
        </div>
      )}
      {!scope ? <p role="status">{t("guide.choose_firm")}</p> : null}
      {blocked && (
        <p role="alert" className="rounded border border-amber-300 p-3">
          {t("guide.unavailable")}
        </p>
      )}
      {scope && !projectId && !requestId && (
        <div>
          <h3 className="block text-sm font-medium" id={projectHeadingId}>
            {t("guide.choose_project")}
          </h3>
          {projects.isError ? (
            <p role="alert">{t("guide.unavailable")}</p>
          ) : onContextChange ? (
            <select
              aria-labelledby={projectHeadingId}
              className="input w-full"
              value=""
              onChange={(e) => {
                if (e.target.value)
                  onContextChange({ projectId: e.target.value, requestId: undefined, companyId });
              }}
            >
              <option value="">{t("guide.choose_project")}</option>
              {projects.data?.items.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.title}
                </option>
              ))}
            </select>
          ) : (
            <ul
              aria-labelledby={projectHeadingId}
              className="max-h-48 space-y-1 overflow-y-auto py-2"
            >
              {projects.data?.items.map((p) => (
                <li key={p.id}>
                  <Link className={button} to={guideHref(p.id)}>
                    {p.title}
                  </Link>
                </li>
              ))}
            </ul>
          )}
          <Link className={button} to="/projects">
            {t("guide.all_projects")}
          </Link>
          <Link className={`${button} ml-2`} to="/requests/new">
            {t("guide.new_project")}
          </Link>
        </div>
      )}
      {verified && (
        <div className="rounded-lg bg-slate-50 p-3 text-sm">
          <p className="break-words font-semibold">{project.data?.title}</p>
          {request.data && <p>{t(`quote_requests.statuses.${request.data.status}`)}</p>}
          {historical && <p>{t("guide.history")}</p>}
          {!historical && completeness.data && project.data?.activity_scope !== "history" && (
            <p>{t(`completeness.next_action.${completeness.data.next_action}`)}</p>
          )}
          {!historical && readiness.data?.blockers.length ? (
            <p role="status">{t("guide.blockers", { count: readiness.data.blockers.length })}</p>
          ) : null}
          {!historical && readiness.data && (
            <SendBlockers blockers={readiness.data.blockers} presentation="compact" />
          )}
          {recommended && (
            <button className={`${button} mt-2`} onClick={() => change(recommended)}>
              {t("guide.recommended")}: {chapters[recommended].title}
            </button>
          )}
          {!requestId && (
            <div className="mt-3">
              <p>{t("guide.choose_request")}</p>
              {requests.isError && <p role="alert">{t("guide.unavailable")}</p>}
              {onContextChange ? (
                <select
                  aria-label={t("guide.choose_request")}
                  className="input w-full"
                  value=""
                  onChange={(e) => {
                    if (e.target.value)
                      onContextChange({
                        projectId,
                        requestId: e.target.value,
                        companyId: project.data?.company_id,
                      });
                  }}
                >
                  <option value="">{t("guide.choose_request")}</option>
                  {requests.data?.items.map((q) => (
                    <option key={q.id} value={q.id}>
                      {q.title ?? t("guide.untitled")} · {t(`quote_requests.statuses.${q.status}`)}
                    </option>
                  ))}
                </select>
              ) : (
                <ul>
                  {requests.data?.items.map((q) => (
                    <li key={q.id}>
                      <Link className={`${button} my-1`} to={guideHref(projectId, q.id)}>
                        {q.title ?? t("guide.untitled")} ·{" "}
                        {t(`quote_requests.statuses.${q.status}`)}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
              <Link className={button} to={`/projects/${projectId}?view=preparation`}>
                {t("guide.open_project")}
              </Link>
            </div>
          )}
        </div>
      )}
      {presentation === "sidebar" ? (
        <label className="block text-sm">
          {t("guide.steps")}
          <select
            className="input mt-1 w-full"
            value={step}
            onChange={(e) => change(e.target.value as GuideStep, false)}
          >
            {GUIDE_STEPS.map((s, i) => (
              <option key={s} value={s}>
                {i + 1}. {chapters[s].title}
              </option>
            ))}
          </select>
        </label>
      ) : (
        <nav aria-label={t("guide.steps")} className="flex flex-wrap gap-2">
          {GUIDE_STEPS.map((s, i) => (
            <button
              key={s}
              className={`${button} ${step === s ? "border-brand-600 bg-brand-50 text-brand-700" : ""}`}
              aria-current={step === s ? "step" : undefined}
              onClick={() => change(s)}
            >
              {i + 1}. {chapters[s].title}
            </button>
          ))}
        </nav>
      )}
      <p className="text-xs text-slate-500">
        {t("guide.reading", { current: index + 1, total: GUIDE_STEPS.length })}
      </p>
      <div aria-live="polite" className="space-y-3">
        <h3
          ref={heading}
          tabIndex={-1}
          className="focus-visible:ring-brand-500 text-lg font-semibold focus-visible:ring-2"
        >
          {chapter.title}
        </h3>
        <p>{chapter.body}</p>
        {scope &&
          !blocked &&
          !projectId &&
          !requestId &&
          step === "client" &&
          (onShowStep ? (
            <button
              type="button"
              className={`${button} bg-brand-600 w-full text-white`}
              onClick={() =>
                onShowStep("/companies", "client", {
                  projectId: undefined,
                  requestId: undefined,
                  companyId,
                })
              }
            >
              {t("assist.show")}
            </button>
          ) : (
            <Link className={button} to="/companies">
              {chapter.action}
            </Link>
          ))}
        {onShowStep && verified && (step === "client" || !!requestId) && (
          <button
            type="button"
            className={`${button} bg-brand-600 w-full text-white`}
            onClick={() =>
              onShowStep(
                historical
                  ? `/projects/${projectId}?view=preparation&request=${requestId}`
                  : guideDestination(
                      step === "followup" ? "review" : step,
                      projectId!,
                      requestId,
                      project.data?.company_id,
                    ),
                historical ? "followup" : step,
                { projectId, requestId, companyId: project.data?.company_id },
              )
            }
          >
            {t("assist.show")}
          </button>
        )}
        {verified && (step === "client" || !!requestId) && (
          <Link
            className={`${button} bg-brand-600 text-white`}
            onClick={() => {
              if (userId && scope && projectId) rememberGuide(userId, scope, projectId, requestId);
            }}
            to={
              historical
                ? `/projects/${projectId}?view=preparation&request=${requestId}`
                : guideDestination(step, projectId!, requestId, project.data?.company_id)
            }
          >
            {historical ? t("guide.view_history") : chapter.action}
          </Link>
        )}
        {!(historical && step === "followup") && (
          <ol className="list-decimal space-y-2 pl-5 text-sm">
            {instructions.steps.map((instruction) => (
              <li key={instruction}>{instruction}</li>
            ))}
          </ol>
        )}
        <details>
          <summary className="focus-visible:ring-brand-500 min-h-11 cursor-pointer py-3 focus-visible:ring-2">
            {t("guide.exception")}
          </summary>
          <p className="text-sm text-slate-600">{chapter.exception}</p>
          {!(historical && step === "followup") && (
            <ol className="mt-2 list-decimal space-y-2 pl-5 text-sm">
              {instructions.missing.map((instruction) => (
                <li key={instruction}>{instruction}</li>
              ))}
            </ol>
          )}
          {(step === "specification" || step === "authorization") && (
            <Link className={`${button} mt-2`} to="/formulaires">
              {t("guide.templates")}
            </Link>
          )}
          {(step === "specification" || step === "authorization") && !blocked && onShowStep && (
            <button
              type="button"
              className={`${button} mt-2`}
              onClick={() =>
                onShowStep("/formulaires", "templates", {
                  projectId,
                  requestId,
                  companyId: project.data?.company_id,
                })
              }
            >
              {t("assist.show_templates")}
            </button>
          )}
        </details>
      </div>
      <div className="flex flex-wrap justify-between gap-2 border-t border-slate-200 pt-3">
        <button
          className={button}
          disabled={index === 0}
          onClick={() => change(GUIDE_STEPS[index - 1] ?? step)}
        >
          {t("guide.previous")}
        </button>
        <button
          className={button}
          disabled={index === GUIDE_STEPS.length - 1}
          onClick={() => change(GUIDE_STEPS[index + 1] ?? step)}
        >
          {t("guide.next")}
        </button>
      </div>
      <p className="text-xs text-slate-500">{t("guide.no_effect")}</p>
    </section>
  );
}

/** Start guidance never loads or targets the previous project's business data. */
function StartGuide({ onShowStep }: Pick<GuideOptions, "onShowStep">) {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const lang = i18n.language.startsWith("en") ? "en" : "fr";
  const chapter = guidedHelp[lang].steps.client;
  return (
    <section aria-label={t("guide.title")} className="min-w-0 space-y-3">
      <h3 className="text-lg font-semibold">{chapter.title}</h3>
      <p>{t("request_flow.client_help")}</p>
      {onShowStep && (
        <button
          type="button"
          className={`${button} bg-brand-600 w-full text-white`}
          onClick={() =>
            onShowStep(
              location.pathname + location.search,
              "start",
              guideContext(location.pathname, location.search),
            )
          }
        >
          {t("assist.show")}
        </button>
      )}
      <ol className="list-decimal space-y-2 pl-5 text-sm">
        {guidedInstructions[lang].client.steps.map((instruction) => (
          <li key={instruction}>{instruction}</li>
        ))}
      </ol>
      <details>
        <summary className="min-h-11 cursor-pointer py-3">{t("guide.exception")}</summary>
        <p>{chapter.exception}</p>
        <ol className="list-decimal space-y-2 pl-5 text-sm">
          {guidedInstructions[lang].client.missing.map((instruction) => (
            <li key={instruction}>{instruction}</li>
          ))}
        </ol>
      </details>
      <p className="text-sm text-slate-600">{t("request_flow.start_help_context")}</p>
      <p className="text-xs text-slate-500">{t("guide.no_effect")}</p>
    </section>
  );
}
